import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { paginateArray } from '@/utils/paginateArray';

const MAX_ENTRIES = 30;

/**
 * Hook for chart pagination. Limits data to MAX_ENTRIES and splits by pageSize.
 * @param {Array} data - Full list of items
 * @param {number} pageSize - Items per page (e.g. 10 desktop, 6 mobile)
 * @returns {{ pageItems: Array, currentPage: number, totalPages: number, goToNextPage: function, goToPreviousPage: function }}
 */
export function useChartPagination(data, pageSize) {
  const [currentPage, setCurrentPage] = useState(0);

  const pagination = useMemo(() => {
    if (!data || data.length === 0) {
      return { totalPages: 0, pages: [], pageIndices: [] };
    }
    const dataToPaginate = (data || []).slice(0, MAX_ENTRIES);
    return paginateArray(dataToPaginate, pageSize);
  }, [data, pageSize]);

  useEffect(() => {
    const maxPage = Math.max(0, pagination.totalPages - 1);
    setCurrentPage(prev => (prev > maxPage ? maxPage : prev));
  }, [pagination.totalPages]);

  const pageItems = pagination.pages[currentPage] || [];
  const { totalPages } = pagination;

  const goToNextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));
  }, [totalPages]);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
  }, []);

  return {
    pageItems,
    currentPage,
    totalPages,
    goToNextPage,
    goToPreviousPage,
  };
}
